<template>
  <div class="scan-stock-out-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">扫码出库</h1>
    </header>
    <main class="content">
      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>
      <div class="form-section source-section">
        <h3 class="section-title">源数据输入</h3>
        <div class="form-group">
          <input 
            id="sourceDataInput"
            v-model="sourceData" 
            placeholder="扫描数据后自动解析" 
            @input="handleInputChange"  
            ref="sourceDataInputRef"
            autocomplete="off"
          />
        </div>
        <div class="source-actions">
          <button @click="handleSourceParse" class="btn parse-btn" :disabled="isParsing">
            <i class="icon-parse"></i> 
            {{ isParsing ? '解析中...' : '解析' }}
          </button>
          <button @click="openManualInput" class="btn manual-input-btn">
            <i class="icon-manual"></i> 手动输入
          </button>
          <button @click="clearSourceData" class="btn clear-btn">
            <i class="icon-clear"></i> 清空
          </button>
        </div>
      </div>
      <div class="form-section card-section">
        <h3 class="section-title">出库数据列表</h3>

        <div v-if="parsedList.length > 0" class="summary-bar">
          <div class="summary-item">
            <span class="summary-label">总箱数：</span>
            <span class="summary-value">{{ parsedList.length }} 箱</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">总数量：</span>
            <span class="summary-value text-primary">{{ totalQuantity.toFixed(2) }} 件</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">物料种类：</span>
            <span class="summary-value">{{ uniqueMaterialCount }} 种</span>
          </div>
        </div>

        <div class="empty-state" v-if="parsedList.length === 0">
          <div class="empty-icon">📦</div>
          <p class="empty-text">暂无出库数据，请扫描或手动输入</p>
        </div>

        <div class="card-list" v-else>
          <div class="data-card" v-for="(item, index) in parsedList" :key="`${item.batchNo}-${index}`">
            <div class="card-header">
              <span class="card-label">箱号：{{ item.batchNo }}</span>
              <button @click="removeCard(index)" class="card-delete" aria-label="删除">×</button>
            </div>
            <div class="card-body">
              <div class="card-row">
                <span class="row-label">物料编码：</span>
                <span class="row-value">{{ item.materialNo }}</span>
              </div>
              <div class="card-row">
                <span class="row-label">数量：</span>
                <span class="row-value">{{ Number(item.quantity).toFixed(2) }}</span>
              </div>
              <div class="card-row">
                <span class="row-label">供应商名称：</span>
                <span class="row-value">{{ item.supplierName }}</span>
              </div>
            </div>
          </div>
        </div>

        <button 
          @click="handleSubmit"
          :disabled="!canSubmit"
          class="submit-btn"
        >
          预览
        </button>
      </div>
    </main>

    <!-- 出库进度弹窗 -->
    <div v-if="isSubmitting" class="modal-overlay">
      <div class="modal-container progress-modal">
        <div class="modal-header">
          <h3 class="modal-title">出库处理中</h3>
        </div>
        
        <div class="modal-body progress-body">
          <div class="progress-stats">
            <span class="stat-item success">
              <span class="stat-icon">✅</span> 成功: {{ currentSuccessCount }}
            </span>
            <span class="stat-item processing">
              <span class="stat-icon">⌛</span> 处理中: {{ currentProcessingCount }}
            </span>
            <span class="stat-item error">
              <span class="stat-icon">❌</span> 失败: {{ currentErrorCount }}
            </span>
            <span class="stat-item total">
              <span class="stat-icon">📊</span> 总计: {{ totalSuppliers }}
            </span>
          </div>
          
          <div class="progress-list">
            <div 
              class="progress-item" 
              v-for="(item, index) in processingProgress" 
              :key="index"
              :class="item.status"
            >
              <div class="progress-item-header">
                <span class="supplier-name">{{ item.supplierName || `供应商 ${index + 1}` }}</span>
                <span class="status-badge">
                  <span v-if="item.status === 'success'">✅ 已完成</span>
                  <span v-if="item.status === 'processing'">⌛ 处理中</span>
                  <span v-if="item.status === 'error'">❌ 失败</span>
                </span>
              </div>
              <div class="progress-item-details">
                <div class="batch-info">
                  批次数量: {{ item.batchCount }} 箱
                </div>
                <div class="error-message" v-if="item.status === 'error' && item.errorMsg">
                  {{ item.errorMsg }}
                </div>
                <div class="progress-bar" v-if="item.status === 'processing'">
                  <div class="progress-indicator" :style="{ width: item.progress + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button 
            @click="cancelSubmission" 
            class="btn cancel-btn"
            :disabled="!canCancel"
          >
            {{ canCancel ? '取消' : '处理中...' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 手动输入弹窗 -->
    <div v-if="showManualInput" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">手动输入出库数据</h3>
          <button @click="closeManualInput" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="batchNo">箱号 <span class="required">*</span></label>
            <input 
              id="batchNo" 
              v-model="manualInput.batchNo" 
              type="text" 
              placeholder="请输入箱号（如B1242522534000103）"
              @input="trimInput('batchNo')"
            >
          </div>
          <div class="form-group">
            <label for="materialNo">物料编码 <span class="required">*</span></label>
            <input 
              id="materialNo" 
              v-model="manualInput.materialNo" 
              type="text" 
              placeholder="请输入物料编码（如P0302-110007）"
              @input="trimInput('materialNo')"
            >
            <p v-if="materialNoInvalid" class="validation-hint">只能包含字母、数字、-和_</p>
          </div>
          <div class="form-group">
            <label for="quantity">数量 <span class="required">*</span></label>
            <input 
              id="quantity" 
              v-model="manualInput.quantity" 
              type="number" 
              step="0.01"
              min="0"
              placeholder="请输入数量（保留两位小数）"
              @input="formatWareOutNum"
            >
          </div>
          <div class="form-group">
            <label for="supplierCode">供应商编码 <span class="required">*</span></label>
            <input 
              id="supplierCode" 
              v-model="manualInput.supplierCode" 
              type="text" 
              placeholder="请输入供应商编码）"
              @input="trimInput('supplierCode')"
            >
          </div>
          <div class="form-group">
            <label for="materialGroupId">物料组ID</label>
            <input 
              id="materialGroupId" 
              v-model="manualInput.materialGroupId" 
              type="text" 
              placeholder="可自定义输入"
              @input="trimInput('materialGroupId')"
            >
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeManualInput" class="btn modal-cancel">取消</button>
          <button @click="confirmManualInput" class="btn modal-confirm" :disabled="!isManualInputValid || isManualSubmitting">
            <span v-if="!isManualSubmitting">确认添加</span>
            <span v-if="isManualSubmitting">验证中...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 预览弹窗 -->
    <div v-if="showPreview" class="modal-overlay">
      <div class="modal-container pda-optimized" style="max-height: 90vh; overflow-y: auto; width: 95vw; max-width: 800px;">
        <div class="modal-header">
          <h3 class="modal-title">出库数据确认</h3>
          <button @click="closePreview" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <div class="preview-summary">
            <h4 class="summary-title">出库汇总信息</h4>
            
            <div class="summary-table-container">
              <table class="summary-table">
                <thead>
                  <tr>
                    <th class="summary-header">物料编码</th>
                    <th class="summary-header">总数量</th>
                    <th class="summary-header">箱数</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="summary-row" v-for="(item, index) in groupedSummary" :key="index">
                    <td class="summary-cell material-cell">{{ item.materialNo }}</td>
                    <td class="summary-cell">{{ Number(item.totalQuantity).toFixed(2) }}</td>
                    <td class="summary-cell">{{ item.boxCount }}</td>
                  </tr>
                  <tr class="summary-row total-row">
                    <td class="summary-cell" colspan="1"><strong>总计</strong></td>
                    <td class="summary-cell"><strong>{{ totalQuantity.toFixed(2) }}</strong></td>
                    <td class="summary-cell"><strong>{{ parsedList.length }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="preview-detail" style="margin-top: 20px;">
            <h4 class="detail-title">出库明细（共 {{ parsedList.length }} 箱）</h4>
            
            <div class="detail-cards">
              <div class="detail-card" v-for="(item, index) in parsedList" :key="`detail-${item.batchNo}-${index}`">
                <div class="detail-card-header">
                  <span class="detail-card-title">箱号：{{ item.batchNo }}</span>
                </div>
                <div class="detail-card-body">
                  <div class="detail-item">
                    <span class="detail-label">物料编码：</span>
                    <span class="detail-value">{{ item.materialNo }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">数量：</span>
                    <span class="detail-value">{{ Number(item.quantity).toFixed(2) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">供应商：</span>
                    <span class="detail-value">{{ item.supplierCode }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closePreview" class="btn modal-cancel">返回修改</button>
          <button @click="confirmStockOut" class="btn modal-confirm" :disabled="isSubmitting">
            <span v-if="!isSubmitting">确认提交出库</span>
            <span v-if="isSubmitting">提交中...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 主机厂选择弹窗 -->
    <div v-if="showFactorySelect" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">选择主机厂、子库和仓库</h3>
          <button @click="showFactorySelect = false" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>主机厂 <span class="required">*</span></label>
            <select v-model="selectedFactory" @change="handleFactoryChange">
              <option value="">请选择主机厂</option>
              <option v-for="factory in factoryList" :key="factory.id" :value="factory">{{ factory.factoryName || factory.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>子库 <span class="required">*</span></label>
            <select v-model="selectedSubWare" @change="fetchWarehouses" :disabled="!selectedFactory">
              <option value="">请选择子库</option>
              <option v-for="subWare in subWareList" :key="subWare.id" :value="subWare">
                {{ subWare.subWareName }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>仓库 <span class="required">*</span></label>
            <select v-model="selectedWarehouse" :disabled="!selectedSubWare">
              <option value="">请选择仓库</option>
              <option v-for="warehouse in warehouseList" :key="warehouse.id" :value="warehouse">{{ warehouse.wareName }}</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showFactorySelect = false" class="btn modal-cancel">取消</button>
          <button @click="confirmFactorySelect" class="btn modal-confirm">确认选择</button>
        </div>
      </div>
    </div>

    <!-- 后端异常提示弹窗 -->
    <div v-if="showErrorModal" class="modal-overlay">
      <div class="modal-container pda-optimized error-modal">
        <div class="modal-header error-header">
          <h3 class="modal-title">操作失败</h3>
          <button @click="showErrorModal = false" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body error-body">
          <div class="error-icon">⚠️</div>
          <div class="error-message">
            <p class="error-title">{{ errorModalTitle }}</p>
            <p class="error-detail" v-if="errorModalDetail">{{ errorModalDetail }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            @click="handleErrorModalAction" 
            class="btn modal-confirm error-confirm"
          >
            {{ errorModalActionText || '确定' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script> 
import { ref, nextTick, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import * as stockOutApi from '@/api/stockOut'

export default {
  name: 'ScanStockOut',
  setup() {
    const router = useRouter()

    // 音频上下文相关变量
    let audioContext = null
    const isAudioContextActive = ref(false)

    // 1. 基础响应式变量
    const sourceData = ref('')
    const parsedList = ref([])
    const statusMsg = ref('')
    const statusType = ref('success')
    const isParsing = ref(false)
    const isSubmitting = ref(false)
    const isManualSubmitting = ref(false)

    // 进度跟踪变量
    const processingProgress = ref([])
    const totalSuppliers = ref(0)
    const currentSuccessCount = ref(0)
    const currentProcessingCount = ref(0)
    const currentErrorCount = ref(0)
    const canCancel = ref(true)
    const submissionAborted = ref(false)

    // 2. 弹窗控制变量
    const showManualInput = ref(false)
    const showPreview = ref(false)
    const showFactorySelect = ref(false)
    const showErrorModal = ref(false)
    const errorModalTitle = ref('')
    const errorModalDetail = ref('')
    const errorModalAdvice = ref('')
    const errorModalActionText = ref('确定')
    const errorModalAction = ref(null)

    // 3. 主机厂/子库/仓库相关变量
    const factoryList = ref([])
    const subWareList = ref([])
    const warehouseList = ref([])
    const selectedFactory = ref(null)
    const selectedSubWare = ref(null)
    const selectedWarehouse = ref(null)
    const bizDept = ref('')

    // 4. 手动输入表单变量
    const manualInput = ref({
      materialNo: '',
      quantity: '',
      supplierCode: '',
      batchNo: '',
      materialGroupId: '',
      remark: ''
    })
    
    const materialNoInvalid = ref(false)

    // 5. 当前时间
    const currentDateTime = computed(() => {
      const now = new Date()
      return now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).replace(/\//g, '-')
    })

    // 6. 计算属性：数据汇总
    const totalQuantity = computed(() => {
      return parsedList.value.reduce((sum, item) => {
        return Number((sum + Number(item.quantity || 0)).toFixed(2))
      }, 0)
    })
    const uniqueMaterialCount = computed(() => {
      const materialSet = new Set(parsedList.value.map(item => item.materialNo))
      return materialSet.size
    })
    const groupedSummary = computed(() => {
      const groups = {};
      parsedList.value.forEach(item => {
        const key = item.materialNo; // 只使用物料编码作为分组键
        if (!groups[key]) {
          groups[key] = {
            materialNo: item.materialNo,
            totalQuantity: 0,
            boxCount: 0
          };
        }
        groups[key].totalQuantity = Number((groups[key].totalQuantity + Number(item.quantity || 0)).toFixed(2));
        groups[key].boxCount += 1;
      });
      return Object.values(groups);
    })

    // 7. 表单验证相关计算属性
    const isManualInputValid = computed(() => {
      const { batchNo, supplierCode, materialNo, quantity } = manualInput.value
      return !!(
        batchNo.trim() &&
        supplierCode.trim() &&
        materialNo.trim() &&
        !materialNoInvalid.value &&
        quantity !== '' && !isNaN(Number(quantity)) && Number(quantity) > 0
      )
    })
    const canSubmit = computed(() => {
      return parsedList.value.length > 0
    })

    // 8. 定时器变量
    let parseTimer = null

    // ------------------------------ 音频相关方法 ------------------------------
    const initAudioContext = () => {
      if (audioContext || isAudioContextActive.value) return

      try {
        const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
        audioContext = new AudioContextConstructor()
        
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            isAudioContextActive.value = true
          }).catch(err => {
            console.warn('激活 AudioContext 失败：', err)
          })
        } else {
          isAudioContextActive.value = true
        }

        document.removeEventListener('click', initAudioContext)
        document.removeEventListener('touchstart', initAudioContext)
      } catch (err) {
        console.warn('创建 AudioContext 失败：', err)
      }
    }

    const playDuplicateBeep = (count = 5) => {
      try {
        if (!audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复 AudioContext 失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(2200, audioContext.currentTime)
        gainNode.gain.setValueAtTime(2, audioContext.currentTime)

        const beepInterval = 200
        for (let i = 0; i < count; i++) {
          setTimeout(() => {
            gainNode.gain.setValueAtTime(2, audioContext.currentTime)
            setTimeout(() => {
              gainNode.gain.setValueAtTime(0, audioContext.currentTime)
            }, 100)
          }, i * beepInterval)
        }

        oscillator.start()
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放提示音失败：', error)
      }
    };

    const playSuccessBeep = () => {
      try {
        if (!audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复 AudioContext 失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(3000, audioContext.currentTime)
        gainNode.gain.setValueAtTime(1, audioContext.currentTime)

        oscillator.start()
        oscillator.stop(audioContext.currentTime + 0.1)
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放成功提示音失败：', error)
      }
    };

    // ------------------------------ 核心方法 ------------------------------
    const showErrorDialog = (title, detail = '', advice = '', actionText = '确定', action = null) => {
      errorModalTitle.value = title
      errorModalDetail.value = detail
      errorModalAdvice.value = advice
      errorModalActionText.value = actionText
      errorModalAction.value = action
      showErrorModal.value = true
    }

    const handleErrorModalAction = () => {
      showErrorModal.value = false
      if (typeof errorModalAction.value === 'function') {
        errorModalAction.value()
      }
    }

    const handleSourceParse = async (singleData = null) => {
      if (isParsing.value) return
      isParsing.value = true

      try {
        await nextTick()
        const cleanData = singleData ? singleData.trim() : (sourceData.value || '').trim()
        if (!cleanData) {
          playDuplicateBeep()
          setStatus('请输入有效的扫描数据', 'warn')
          return
        }

        if (cleanData.includes('\n') || cleanData.includes('\r')) {
          setStatus('解析失败：数据包含多条记录，请分开扫描', 'error')
          return
        }

        const parseResult = await parseSourceData(cleanData)
        if (!parseResult.success) {
          playDuplicateBeep()
          showErrorDialog(
            '解析失败',
            parseResult.errorMsg,
            '请检查扫描数据格式是否正确',
            '返回修改'
          )
          if (!singleData) sourceData.value = ''
          return
        }

        const isDuplicate = parsedList.value.some(item => item.batchNo === parseResult.data.batchNo)
        if (isDuplicate) {
          playDuplicateBeep()
          showErrorDialog(
            '重复添加提示',
            `箱号 ${parseResult.data.batchNo} 已添加，请勿重复解析`,
            '请检查扫码数据或选择其他箱号',
            '确定'
          );
          // setStatus(`箱号 ${parseResult.data.batchNo} 已添加，请勿重复解析`, 'warn')
          if (!singleData) sourceData.value = ''
          return
        }

        parsedList.value.unshift(parseResult.data)
        playSuccessBeep()
        setStatus(`解析成功！已添加 ${parsedList.value.length} 箱，总数量 ${totalQuantity.value.toFixed(2)} 件`, 'success')
        if (!singleData) sourceData.value = ''
        document.getElementById('sourceDataInput')?.focus()
      } catch (err) {
        console.error('handleSourceParse error:', err)
      } finally {
        isParsing.value = false
      }
    }

    // const parseSourceData = async (data) => {
    //   try {
    //     if (typeof data !== 'string') {
    //       playDuplicateBeep()
    //       return { success: false, errorMsg: '解析失败：输入数据不是字符串' }
    //     }

    //     const normalizedData = data.replace(/×/g, '*')
    //     const fields = normalizedData.split('*')

    //     if (fields.length < 4) {
    //       playDuplicateBeep()
    //       return { success: false, errorMsg: `数据格式错误！至少需要4个字段（当前拆分为${fields.length}个）` }
    //     }

    //     const materialNo = (fields[0] || '').trim()
    //     const quantity = (fields[1] || '').trim()
    //     const supplierCode = (fields[2] || '').trim()
    //     const batchNo = (fields[fields.length - 1] || '').trim()

    //     if (!quantity || isNaN(Number(quantity))) {
    //       return { success: false, errorMsg: '数量（第2个字段）必须是有效数字' }
    //     }
    //     const formattedQuantity = Number(quantity).toFixed(2)

    //     if (!materialNo) return { success: false, errorMsg: '物料编码（第1个字段）不能为空' }
    //     if (!/^[A-Za-z0-9\-_]+$/.test(materialNo)) {
    //       return { success: false, errorMsg: `物料编码格式无效：${materialNo}，只能包含字母、数字、-和_` }
    //     }
    //     if (!supplierCode) return { success: false, errorMsg: '供应商编码（第3个字段）不能为空' }
    //     if (!batchNo) return { success: false, errorMsg: '箱号（最后1个字段）不能为空' }

    //     try {
    //       const supplierRes = await stockOutApi.getSupplierByCode(supplierCode)
    //       if (!supplierRes.success || !supplierRes.data?.records || supplierRes.data.records.length === 0) {
    //         return { success: false, errorMsg: `供应商 ${supplierCode} 不存在，请检查编码` }
    //       }
    //       const supplierId = supplierRes.data.records[0].id
    //       const supplierName = supplierRes.data.records[0].supplierName
          
    //       if (supplierRes.data.records[0].bizDept && !bizDept.value) {
    //         bizDept.value = supplierRes.data.records[0].bizDept
    //       }

    //       const materialRes = await stockOutApi.getMaterialBySupplierAndNo(materialNo, supplierId)
    //       if (!materialRes.success || !materialRes.data?.records || materialRes.data.records.length === 0) {
    //         playDuplicateBeep()
    //         return { success: false, errorMsg: `物料 ${materialNo} 不存在于供应商 ${supplierCode} 下` }
    //       }
    //       const materialData = materialRes.data.records[0]

    //       const stockRes = await stockOutApi.checkMaterialStock(materialData.id, batchNo)
    //       if (!stockRes.success || !stockRes.data) {
    //         return { 
    //           success: false, 
    //           errorMsg: stockRes.message || `物料 ${materialNo} 批次 ${batchNo} 无可用库存` 
    //         }
    //       }

    //       const materialGroupId = materialData.materialGroupId || `GRP-${supplierCode}-${materialNo.substring(0, 5)}`

    //       return {
    //         success: true,
    //         data: {
    //           materialNo,
    //           materialId: materialData.id,
    //           materialGroupId,
    //           quantity: formattedQuantity,
    //           supplierCode,
    //           supplierId,
    //           supplierName,
    //           batchNo
    //         }
    //       }
    //     } catch (apiErr) {
    //       return { 
    //         success: false, 
    //         errorMsg: `API调用失败: ${apiErr.message || '无法连接到服务器，请检查网络'}` 
    //       }
    //     }
    //   } catch (err) {
    //     return { success: false, errorMsg: `数据解析异常：${err.message || '未知错误'}` }
    //   }
    // }
    const parseSourceData = async (data) => {
  try {
    if (typeof data !== 'string') {
      playDuplicateBeep()
      return { success: false, errorMsg: '解析失败：输入数据不是字符串' }
    }

    const normalizedData = data.replace(/×/g, '*')
    // 关键修改1：拆分后过滤末尾空字段（解决多出来的*导致的空字段问题）
    const fields = normalizedData.split('*').filter(field => field.trim() !== '')

    // 关键修改2：放宽字段数限制（4-20个字段足够兼容现有及未来扩展格式）
    if (fields.length < 4 || fields.length > 20) {
      playDuplicateBeep()
      return { success: false, errorMsg: `数据格式错误！需要4-20个字段（当前拆分为${fields.length}个）` }
    }

    // 核心解析逻辑不变（仍取第1个物料编码、第2个数量、第3个供应商编码、最后1个箱号）
    const materialNo = (fields[0] || '').trim()
    const quantity = (fields[1] || '').trim()
    const supplierCode = (fields[2] || '').trim()
    const batchNo = (fields[fields.length - 1] || '').trim()

    // 后续校验逻辑保持不变...
    if (!quantity || isNaN(Number(quantity))) {
      return { success: false, errorMsg: '数量（第2个字段）必须是有效数字' }
    }
    const formattedQuantity = Number(quantity).toFixed(2)

    if (!materialNo) return { success: false, errorMsg: '物料编码（第1个字段）不能为空' }
    if (!/^[A-Za-z0-9\-_]+$/.test(materialNo)) {
      return { success: false, errorMsg: `物料编码格式无效：${materialNo}，只能包含字母、数字、-和_` }
    }
    if (!supplierCode) return { success: false, errorMsg: '供应商编码（第3个字段）不能为空' }
    if (!batchNo) return { success: false, errorMsg: '箱号（最后1个字段）不能为空' }

    // 以下API调用和返回逻辑完全不变...
    try {
      const supplierRes = await stockOutApi.getSupplierByCode(supplierCode)
      if (!supplierRes.success || !supplierRes.data?.records || supplierRes.data.records.length === 0) {
        return { success: false, errorMsg: `供应商 ${supplierCode} 不存在，请检查编码` }
      }
      const supplierId = supplierRes.data.records[0].id
      const supplierName = supplierRes.data.records[0].supplierName
      
      if (supplierRes.data.records[0].bizDept && !bizDept.value) {
        bizDept.value = supplierRes.data.records[0].bizDept
      }

      const materialRes = await stockOutApi.getMaterialBySupplierAndNo(materialNo, supplierId)
      if (!materialRes.success || !materialRes.data?.records || materialRes.data.records.length === 0) {
        playDuplicateBeep()
        return { success: false, errorMsg: `物料 ${materialNo} 不存在于供应商 ${supplierCode} 下` }
      }
      const materialData = materialRes.data.records[0]

      const stockRes = await stockOutApi.checkMaterialStock(materialData.id, batchNo)
      if (!stockRes.success || !stockRes.data) {
        return { 
          success: false, 
          errorMsg: stockRes.message || `物料 ${materialNo} 批次 ${batchNo} 无可用库存` 
        }
      }

      const materialGroupId = materialData.materialGroupId || `GRP-${supplierCode}-${materialNo.substring(0, 5)}`

      return {
        success: true,
        data: {
          materialNo,
          materialId: materialData.id,
          materialGroupId,
          quantity: formattedQuantity,
          supplierCode,
          supplierId,
          supplierName,
          batchNo
        }
      }
    } catch (apiErr) {
      return { 
        success: false, 
        errorMsg: `API调用失败: ${apiErr.message || '无法连接到服务器，请检查网络'}` 
      }
    }
  } catch (err) {
    return { success: false, errorMsg: `数据解析异常：${err.message || '未知错误'}` }
  }
}

    /**
     * 确认出库提交 - 修复要点：
     * 1. 点击后立即关闭预览界面
     * 2. 移除所有重试机制
     */
    const confirmStockOut = async () => {
      if (!canSubmit.value) return;

      try {
        // 修复1：点击后立即关闭预览界面
        showPreview.value = false;
        isSubmitting.value = true;
        setStatus('正在初始化出库提交...', 'info');

        // 基础校验
        if (!parsedList.value.length || !parsedList.value[0].supplierId) {
          showErrorDialog(
            '数据不完整',
            '缺少供应商信息，请重新解析数据',
            '请删除当前数据并重新扫描'
          )
          isSubmitting.value = false;
          return;
        }

        // 按供应商ID分组
        const supplierGroups = {};
        parsedList.value.forEach(item => {
          const supplierId = item.supplierId;
          if (!supplierGroups[supplierId]) {
            supplierGroups[supplierId] = [];
          }
          supplierGroups[supplierId].push(item);
        });
        const supplierIds = Object.keys(supplierGroups);
        if (supplierIds.length === 0) {
          showErrorDialog(
            '数据异常',
            '未检测到有效供应商数据',
            '请检查数据后重新提交'
          )
          isSubmitting.value = false;
          return;
        }

        // 初始化进度跟踪
        processingProgress.value = supplierIds.map(supplierId => {
          const groupData = supplierGroups[supplierId];
          const firstItem = groupData[0];
          return {
            supplierId,
            supplierName: firstItem.supplierName || firstItem.supplierCode,
            batchCount: groupData.length,
            status: 'pending',
            progress: 0,
            errorMsg: ''
          };
        });
        
        totalSuppliers.value = supplierIds.length;
        currentSuccessCount.value = 0;
        currentProcessingCount.value = 0;
        currentErrorCount.value = 0;
        submissionAborted.value = false;
        canCancel.value = true;

        // 配置请求间隔
        const REQUEST_INTERVAL = 5 * 1000;
        const TOTAL_SUPPLIERS = supplierIds.length;

        // 提交确认提示
        const confirmMsg = `共检测到 ${TOTAL_SUPPLIERS} 个供应商的数据，将分 ${TOTAL_SUPPLIERS} 次提交。`;
        if (!window.confirm(confirmMsg)) {
          isSubmitting.value = false;
          return;
        }

        // 结果统计变量
        let successCount = 0;
        const failRecords = [];
        const allWareOutIds = [];

        // 带倒计时的等待函数
        const waitWithCountdown = async (ms, currentProgress) => {
          const totalSeconds = Math.floor(ms / 1000);
          let remainingSeconds = totalSeconds;

          return new Promise(resolve => {
            const countdownTimer = setInterval(() => {
              remainingSeconds--;
              setStatus(`等待下一个供应商提交（${currentProgress}），剩余时间：${remainingSeconds} 秒`, 'success');

              if (remainingSeconds <= 0) {
                clearInterval(countdownTimer);
                setStatus(`等待结束，开始处理下一个供应商（${currentProgress}）...`, 'success');
                resolve();
              }
            }, 1000);
          });
        };

        // 循环处理每个供应商的出库请求
        for (let i = 0; i < supplierIds.length; i++) {
          if (submissionAborted.value) {
            setStatus(`出库操作已取消，已处理 ${i}/${TOTAL_SUPPLIERS} 个供应商`, 'warn');
            break;
          }
          
          const supplierId = supplierIds[i];
          const groupData = supplierGroups[supplierId];
          const firstItem = groupData[0];
          const currentProgress = `${i + 1}/${TOTAL_SUPPLIERS}`;
          const isFirstRequest = i === 0;

          // 更新当前供应商状态为处理中
          const progressIndex = processingProgress.value.findIndex(p => p.supplierId === supplierId);
          if (progressIndex !== -1) {
            processingProgress.value[progressIndex].status = 'processing';
            processingProgress.value[progressIndex].progress = 30;
            currentProcessingCount.value++;
          }

          // 非首次请求：执行间隔等待
          if (!isFirstRequest) {
            await waitWithCountdown(REQUEST_INTERVAL, currentProgress);
          }

          // 构建当前供应商的出库参数
          const submitData = {
            bizDept: selectedFactory.value.bizDept,
            factoryId: selectedFactory.value.id,
            factoryName: selectedFactory.value.factoryName,
            subWareId: selectedSubWare.value.id,
            storeId: selectedWarehouse.value.id,
            isBad: 0,
            supplierId: supplierId,
            demandTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
            remark: `自动提交于${new Date().toLocaleString()}`,
            outDetailList: groupData.map(item => ({
              materialId: item.materialId,
              stockNum: item.quantity,
              supplierCode: item.supplierCode,
              supplierId: item.supplierId,
              batchNo: item.batchNo,
              materialGroupId: item.materialGroupId,
              storeId: selectedWarehouse.value.id,
              subWareId: selectedSubWare.value.id,
              wareOutNum: item.quantity,
            }))
          };

          // 提交出库单
          setStatus(`正在提交【${firstItem.supplierName || '未知供应商'}】的出库数据（${currentProgress}，共 ${groupData.length} 箱）...`, 'success');
          
          try {
              const response = await stockOutApi.submitSendPlan(submitData);
              
              if (!response.success) {
                // 后端返回失败状态
                const failMsg = `【${firstItem.supplierName || '未知供应商'}】提交失败: ${response.msg || '操作失败'}`;
                failRecords.push(failMsg);
                
                // 更新进度为失败
                if (progressIndex !== -1) {
                  processingProgress.value[progressIndex].status = 'error';
                  processingProgress.value[progressIndex].progress = 100;
                  processingProgress.value[progressIndex].errorMsg = failMsg;
                  currentErrorCount.value++;
                }

                // 修复2：移除重试机制，仅显示错误信息，继续处理下一个
                showErrorDialog(
                  '提交失败',
                  failMsg,
                  '此供应商数据提交失败，将继续处理其他供应商',
                  '继续',
                  () => {}
                );
                continue;
              }

            const wareOutId = response.data.wareOutId;
            if (!wareOutId) {
              const warnMsg = `【${firstItem.supplierName || '未知供应商'}】出库成功，但未获取到出库单ID`;
              failRecords.push(warnMsg);
              setStatus(warnMsg, 'warn');
              
              // 更新进度为成功
              if (progressIndex !== -1) {
                processingProgress.value[progressIndex].status = 'success';
                processingProgress.value[progressIndex].progress = 100;
                currentSuccessCount.value++;
              }
              successCount++;
              continue;
            }

            allWareOutIds.push(wareOutId);
            setStatus(`【${firstItem.supplierName || '未知供应商'}】提交成功！出库单ID：${wareOutId}`, 'success');

            // 执行“立即出库”
            try {
              const executeResponse = await stockOutApi.immediateWareout({ id: wareOutId });
              if (executeResponse.success) {
                setStatus(`【${firstItem.supplierName || '未知供应商'}】出库单(${wareOutId})执行成功`, 'success');
                
                // 更新进度为成功
                if (progressIndex !== -1) {
                  processingProgress.value[progressIndex].status = 'success';
                  processingProgress.value[progressIndex].progress = 100;
                  currentSuccessCount.value++;
                }
              } else {
                const warnMsg = `【${firstItem.supplierName || '未知供应商'}】出库单(${wareOutId})执行失败: ${executeResponse.msg}`;
                failRecords.push(warnMsg);
                
                // 更新进度为成功（因为出库单已创建）
                if (progressIndex !== -1) {
                  processingProgress.value[progressIndex].status = 'success';
                  processingProgress.value[progressIndex].progress = 100;
                  processingProgress.value[progressIndex].errorMsg = `出库单已创建，但执行失败: ${executeResponse.msg}`;
                  currentSuccessCount.value++;
                }
                
                showErrorDialog(
                  '执行失败',
                  warnMsg,
                  '出库单已创建但执行失败，请记录后手动处理',
                  '继续'
                )
              }
              successCount++;
            } catch (processErr) {
              const warnMsg = `【${firstItem.supplierName || '未知供应商'}】出库单(${wareOutId})提交成功，但执行出库失败：${processErr.message || '未知错误'}`;
              failRecords.push(warnMsg);
              
              // 更新进度为成功（因为出库单已创建）
              if (progressIndex !== -1) {
                processingProgress.value[progressIndex].status = 'success';
                processingProgress.value[progressIndex].progress = 100;
                processingProgress.value[progressIndex].errorMsg = warnMsg;
                currentSuccessCount.value++;
              }
              
              showErrorDialog(
                '执行异常',
                warnMsg,
                '请记录后手动执行该出库单',
                '继续'
              )
              successCount++;
            }
          } catch (submitErr) {
            // 提交过程中发生异常
            const errorMsg = `【${firstItem.supplierName || '未知供应商'}】提交异常: ${submitErr.message || '网络错误或服务器无响应'}`;
            failRecords.push(errorMsg);
            
            // 更新进度为失败
            if (progressIndex !== -1) {
              processingProgress.value[progressIndex].status = 'error';
              processingProgress.value[progressIndex].progress = 100;
              processingProgress.value[progressIndex].errorMsg = errorMsg;
              currentErrorCount.value++;
            }
            
            // 修复2：移除重试机制，显示错误后继续处理下一个
            showErrorDialog(
              '提交异常',
              errorMsg,
              '将继续处理其他供应商',
              '继续',
              () => {}
            );
            
            // 等待用户点击继续
            await new Promise(resolve => {
              const checkInterval = setInterval(() => {
                if (!showErrorModal.value) {
                  clearInterval(checkInterval);
                  resolve();
                }
              }, 100);
            });
          } finally {
            // 减少处理中计数
            if (progressIndex !== -1) {
              currentProcessingCount.value--;
            }
          }
        }

        // 所有供应商处理完成：统一结果提示
        if (submissionAborted.value) {
          setStatus(`出库操作已取消，已处理 ${successCount}/${TOTAL_SUPPLIERS} 个供应商`, 'warn');
        } else if (successCount === TOTAL_SUPPLIERS && failRecords.length === 0) {
          setStatus(`全部完成！${successCount}/${TOTAL_SUPPLIERS} 个供应商的出库数据提交成功，即将返回上一页`, 'success');
          parsedList.value = [];
          setTimeout(() => router.back(), 3000);
        } else if (successCount > 0) {
          const failDetail = failRecords.join('；\n');
          showErrorDialog(
            '部分完成',
            `成功：${successCount}/${TOTAL_SUPPLIERS} 个供应商\n失败详情：\n${failDetail}`,
            '请检查失败项并重新提交',
            '返回',
            () => {
              parsedList.value = [];
              isSubmitting.value = false;
            }
          )
        } else {
          showErrorDialog(
            '全部失败',
            `所有 ${TOTAL_SUPPLIERS} 个供应商的出库数据均提交失败：\n${failRecords.join('；')}`,
            '请检查网络和数据后重试',
            '返回修改',
            () => {
              isSubmitting.value = false;
            }
          )
        }

      } catch (globalErr) {
        showErrorDialog(
          '系统异常',
          `出库请求发生严重错误：${globalErr.response?.data?.msg || globalErr.message || '未知错误'}`,
          '请联系技术支持',
          '返回',
          () => {
            showPreview.value = false;
            isSubmitting.value = false;
          }
        )
        console.error('confirmStockOut global error:', globalErr);
      } finally {
        if (!showErrorModal.value) {
          isSubmitting.value = false;
        }
      }
    };

    const cancelSubmission = () => {
      if (!canCancel.value) return
      
      if (window.confirm('确定要取消出库操作吗？已处理的部分将保留，未处理的将终止。')) {
        submissionAborted.value = true
        canCancel.value = false
        setStatus('正在终止出库操作...', 'info')
      }
    }

    const setStatus = (message, type = 'success') => {
      statusMsg.value = message
      statusType.value = type
      setTimeout(() => {
        if (statusMsg.value === message) {
          statusMsg.value = ''
        }
      }, 3000)
    }

    const handleInputChange = () => {
      if (parseTimer) clearTimeout(parseTimer)
      const currentData = (sourceData.value || '').trim()
      
      if (currentData.includes('\n') || currentData.includes('\r')) {
        const lineBreakMatch = currentData.match(/[\n\r]/)
        if (lineBreakMatch) {
          const firstData = currentData.substring(0, lineBreakMatch.index).trim()
          if (firstData) {
            if (parseTimer) clearTimeout(parseTimer)
            sourceData.value = currentData.substring(lineBreakMatch.index + 1).trim()
            handleSourceParse(firstData)
          }
        }
      } else if (currentData.length > 15) {
        parseTimer = setTimeout(() => {
          handleSourceParse()
        }, 300)
      }
    }

    const clearSourceData = () => {
      sourceData.value = ''
      if (parseTimer) clearTimeout(parseTimer)
      document.getElementById('sourceDataInput')?.focus()
    }

    const removeCard = (index) => {
      const removedItem = parsedList.value.splice(index, 1)[0]
      setStatus(`已删除箱号 ${removedItem.batchNo} 的数据`, 'warn')
    }

    const formatWareOutNum = () => {
      let num = Number(manualInput.value.quantity)
      if (isNaN(num) || num <= 0) num = 0
      manualInput.value.quantity = Number(num.toFixed(2))
    }

    const trimInput = (field) => {
      if (typeof manualInput.value[field] === 'string') {
        manualInput.value[field] = manualInput.value[field].trim()
        
        if (field === 'materialNo') {
          materialNoInvalid.value = !/^[A-Za-z0-9\-_]*$/.test(manualInput.value[field])
        }
      } else {
        manualInput.value[field] = (manualInput.value[field] || '').toString().trim()
      }
    }

    const openManualInput = () => {
      manualInput.value = {
        materialNo: '',
        quantity: 1.00,
        supplierCode: '',
        batchNo: '',
        materialGroupId: '',
        remark: ''
      }
      materialNoInvalid.value = false
      showManualInput.value = true
      setTimeout(() => {
        document.getElementById('materialNo')?.focus()
      }, 300)
    }
    const closeManualInput = () => showManualInput.value = false

    const confirmManualInput = async () => {
      if (!isManualInputValid.value) {
        setStatus('请完善所有必填项，且数量必须是有效数字', 'warn')
        return
      }

      isManualSubmitting.value = true
      try {
        const { batchNo, materialNo, supplierCode, quantity } = manualInput.value
        const cleanBatchNo = batchNo.trim()
        const cleanMaterialNo = materialNo.trim()
        const cleanSupplierCode = supplierCode.trim()
        const cleanQuantity = Number(quantity).toFixed(2)

        const isDuplicate = parsedList.value.some(
          item => (item.batchNo || '').trim() === cleanBatchNo
        )
        if (isDuplicate) {
          playDuplicateBeep()
          showErrorDialog(
            '重复添加',
            `箱号 ${cleanBatchNo} 已存在，请勿重复添加`,
            '请检查箱号后重新输入',
            '确定',
            () => {
              showManualInput.value = false;
              setTimeout(() => document.getElementById('batchNo')?.focus(), 300);
            }
          )
          return
        }

        try {
          const supplierRes = await stockOutApi.getSupplierByCode(cleanSupplierCode)
          if (!supplierRes.success || !supplierRes.data?.records || supplierRes.data.records.length === 0) {
            showErrorDialog(
              '供应商不存在',
              `供应商 ${cleanSupplierCode} 不存在，请检查编码`,
              '请确认供应商编码是否正确'
            )
            return
          }
          const supplierId = supplierRes.data.records[0].id
          const supplierName = supplierRes.data.records[0].supplierName

          const materialRes = await stockOutApi.getMaterialBySupplierAndNo(cleanMaterialNo, supplierId)
          if (!materialRes.success || !materialRes.data?.records || materialRes.data.records.length === 0) {
            showErrorDialog(
              '物料不存在',
              `物料 ${cleanMaterialNo} 不存在于供应商 ${cleanSupplierCode} 下`,
              '请确认物料编码和供应商是否匹配'
            )
            return
          }
          const materialData = materialRes.data.records[0]

          const materialGroupId = manualInput.value.materialGroupId || 
                                `GRP-${cleanSupplierCode}-${cleanMaterialNo.substring(0, 5)}`

          parsedList.value.push({
            materialNo: cleanMaterialNo,
            materialId: materialData.id,
            materialGroupId,
            quantity: cleanQuantity,
            supplierCode: cleanSupplierCode,
            supplierId: supplierId,
            supplierName: supplierName,
            batchNo: cleanBatchNo
          })

          setStatus(`手动添加成功！当前共 ${parsedList.value.length} 箱，总数量 ${totalQuantity.value.toFixed(2)} 件`, 'success')
          showManualInput.value = false
          document.getElementById('sourceDataInput')?.focus()
        } catch (apiErr) {
          showErrorDialog(
            '服务异常',
            `获取数据失败: ${apiErr.message || '网络错误或服务器无响应'}`,
            '请检查网络连接后重试',
            '重试',
            confirmManualInput
          )
        }
      } catch (err) {
        showErrorDialog(
          '操作失败',
          `手动添加失败：${err.message || '服务器异常'}`,
          '请稍后重试',
          '确定'
        )
        console.error('confirmManualInput error:', err)
      } finally {
        isManualSubmitting.value = false
      }
    }

    const fetchFactories = async () => {
      try {
        const res = await stockOutApi.getFactories({ bizDept: bizDept.value })
        if (res.success && Array.isArray(res.data)) {
          factoryList.value = res.data
          if (res.data.length > 0) {
            selectedFactory.value = res.data[0]
            subWareList.value = res.data[0].subWareList || []
            await nextTick()
            if (subWareList.value.length > 0) {
              selectedSubWare.value = subWareList.value[0]
              fetchWarehouses()
            }
          }
        } else {
          showErrorDialog(
            '数据加载失败',
            '获取主机厂列表失败',
            '请刷新页面重试'
          )
        }
      } catch (err) {
        console.error('获取主机厂失败:', err)
        showErrorDialog(
          '网络异常',
          '获取主机厂列表时发生错误',
          '请检查网络连接'
        )
      }
    }

    const fetchWarehouses = async () => {
      try {
        if (!selectedSubWare.value) return
        
        const res = await stockOutApi.getWarehouses({
          subWareId: selectedSubWare.value.id,
          wareType: 0,
          bizDept: bizDept.value,
          current: 1,
          size: 100
        })
        
        if (res.success && res.data && Array.isArray(res.data.records)) {
          warehouseList.value = res.data.records
          if (warehouseList.value.length > 0) {
            selectedWarehouse.value = warehouseList.value[0]
          } else {
            selectedWarehouse.value = null
            setStatus('未查询到仓库数据', 'warn')
          }
        } else {
          warehouseList.value = []
          selectedWarehouse.value = null
          showErrorDialog(
            '数据异常',
            '仓库数据格式不正确',
            '请联系系统管理员'
          )
        }
      } catch (err) {
        console.error('获取仓库失败:', err)
        showErrorDialog(
          '网络异常',
          '获取仓库列表时发生错误',
          '请检查网络连接'
        )
        warehouseList.value = []
      }
    }

    const handleFactoryChange = async () => {
      if (!selectedFactory.value) return
      subWareList.value = selectedFactory.value.subWareList || []
      selectedSubWare.value = null
      selectedWarehouse.value = null
      warehouseList.value = []
      await nextTick()
      if (subWareList.value.length > 0) {
        selectedSubWare.value = subWareList.value[0]
        fetchWarehouses()
      }
    }

    const openPreview = () => {
      if (parsedList.value.length === 0) {
        setStatus('暂无出库数据可预览', 'warn')
        return
      }
      showPreview.value = true
    }
    const closePreview = () => showPreview.value = false

    const handleSubmit = async () => {
      if (parsedList.value.length === 0) {
        setStatus('请先完善出库数据', 'warn')
        return
      }
      
      if (!bizDept.value && parsedList.value.length > 0) {
        const firstItem = parsedList.value[0]
        try {
          const supplierRes = await stockOutApi.getSupplierByCode(firstItem.supplierCode)
          if (supplierRes.success && supplierRes.data?.records && supplierRes.data.records.length > 0) {
            if (supplierRes.data.records[0].bizDept) {
              bizDept.value = supplierRes.data.records[0].bizDept
            }
          }
        } catch (err) {
          console.error('获取供应商信息失败:', err)
        }
      }
      
      if (!bizDept.value) {
        console.warn('未获取到bizDept，使用默认值')
      }
      
      showFactorySelect.value = true
      fetchFactories()
    }

    const confirmFactorySelect = () => {
      if (!selectedFactory.value) {
        setStatus('请选择主机厂', 'warn')
        return
      }
      
      if (!selectedSubWare.value) {
        setStatus('请选择子库', 'warn')
        return
      }
      
      if (!selectedWarehouse.value) {
        setStatus('请选择仓库', 'warn')
        return
      }
      
      showFactorySelect.value = false
      openPreview()
    }

    const handleBack = () => router.back()

    onMounted(() => {
      document.getElementById('sourceDataInput')?.focus()
      
      document.addEventListener('click', initAudioContext, { once: true })
      document.addEventListener('touchstart', initAudioContext, { once: true })
    })

    watch(() => showManualInput.value, (newVal) => {
      if (!newVal && parseTimer) {
        clearTimeout(parseTimer)
      }
    })

    return {
      sourceData,
      parsedList,
      statusMsg,
      statusType,
      isParsing,
      isSubmitting,
      isManualSubmitting,
      showManualInput,
      showPreview,
      showFactorySelect,
      showErrorModal,
      errorModalTitle,
      errorModalDetail,
      errorModalAdvice,
      errorModalActionText,
      factoryList,
      subWareList,
      warehouseList,
      selectedFactory,
      selectedSubWare,
      selectedWarehouse,
      manualInput,
      materialNoInvalid,
      totalQuantity,
      uniqueMaterialCount,
      groupedSummary,
      isManualInputValid,
      canSubmit,
      currentDateTime,
      handleSourceParse,
      handleInputChange,
      clearSourceData,
      removeCard,
      openManualInput,
      closeManualInput,
      confirmManualInput,
      trimInput,
      openPreview,
      closePreview,
      confirmStockOut,
      handleBack,
      handleSubmit,
      confirmFactorySelect,
      handleFactoryChange,
      fetchWarehouses,
      showErrorDialog,
      handleErrorModalAction,
      playDuplicateBeep,
      formatWareOutNum,
      processingProgress,
      totalSuppliers,
      currentSuccessCount,
      currentProcessingCount,
      currentErrorCount,
      canCancel,
      cancelSubmission
    }
  }
}
</script>


<!-- <style scoped>
/* 原有样式 */
.scan-stock-out-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-color);
}

.header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
}

.back-btn {
  background: none;
  border: none;
  color: white;
  padding: 4px;
  margin-right: 12px;
  cursor: pointer;
}

.icon-back {
  width: 24px;
  height: 24px;
}

.title {
  font-size: 18px;
  font-weight: 500;
  flex: 1;
  text-align: center;
  margin: 0;
}

.content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.form-section {
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 16px 0;
  color: var(--text-primary);
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-secondary);
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  background-color: var(--card-background);
  color: var(--text-primary);
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.2);
}

.required {
  color: #ef4444;
}

.validation-hint {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: #f5222d;
}

.source-actions {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 10px 16px;
  border-radius: 4px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.parse-btn {
  background-color: var(--primary-color);
  color: white;
}

.parse-btn:disabled {
  background-color: rgba(35, 177, 192, 0.5);
  cursor: not-allowed;
}

.manual-input-btn {
  background-color: #10b981;
  color: white;
}

.clear-btn {
  background-color: #ef4444;
  color: white;
}

.modal-cancel {
  background-color: var(--card-background);
  color: var(--text-primary);
}

.modal-confirm {
  background-color: var(--primary-color);
  color: white;
}

.status-banner {
  padding: 10px 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
}

.status-banner.success {
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.status-banner.warn {
  background-color: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.status-banner.error {
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-banner.info {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  border: 1px solid rgba(35, 177, 192, 0.3);
}

.summary-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.summary-item {
  display: flex;
  align-items: center;
}

.summary-label {
  color: #666;
  margin-right: 4px;
  font-size: 14px;
}

.summary-value {
  font-weight: 500;
  font-size: 14px;
}

.text-primary {
  color: #1890ff;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 14px;
  margin: 0;
}

.card-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.data-card {
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.data-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #eee;
}

.card-label {
  font-weight: 500;
  font-size: 14px;
}

.card-delete {
  background: none;
  border: none;
  color: #999;
  font-size: 16px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s, color 0.2s;
}

.card-delete:hover {
  background-color: #ffeeee;
  color: #f5222d;
}

.card-body {
  padding: 12px 16px;
}

.card-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
}

.row-label {
  color: #666;
  min-width: 80px;
}

.row-value {
  flex: 1;
  color: #333;
  word-break: break-all;
}

.submit-btn {
  width: 100%;
  padding: 12px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.submit-btn:disabled {
  background-color: #8cc5ff;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  width: 90vw;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #eee;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: #f5f5f5;
}

.modal-body {
  padding: 16px;
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
}

/* 预览弹窗样式 */
.preview-summary {
  margin-bottom: 20px;
}

.summary-title {
  font-size: 16px;
  margin: 0 0 12px 0;
  color: #333;
}

.summary-table-container {
  overflow-x: auto;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.summary-header {
  background-color: #f5f5f5;
  padding: 10px;
  text-align: left;
  border: 1px solid #eee;
  font-weight: 500;
}

.summary-row {
  border-bottom: 1px solid #eee;
}

.summary-cell {
  padding: 10px;
  border: 1px solid #eee;
}

.total-row {
  background-color: #fafafa;
}

.detail-title {
  font-size: 16px;
  margin: 0 0 12px 0;
  color: #333;
}

.detail-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.detail-card {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 12px;
}

.detail-card-header {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.detail-card-title {
  font-weight: 500;
  font-size: 14px;
}

.detail-item {
  display: flex;
  margin-bottom: 6px;
  font-size: 13px;
}

.detail-label {
  color: #666;
  min-width: 70px;
}

.detail-value {
  flex: 1;
  word-break: break-all;
}

/* 错误弹窗样式 */
.error-modal {
  max-width: 400px;
}

.error-header {
  background-color: #fff2f0;
  border-bottom: 1px solid #ffccc7;
}

.error-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  text-align: center;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
  color: #f5222d;
}

.error-title {
  font-size: 16px;
  font-weight: 500;
  color: #f5222d;
  margin: 0 0 8px 0;
}

.error-detail {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.error-confirm {
  background-color: #f5222d;
  color: white;
}

/* 进度弹窗样式 */
.progress-modal {
  width: 90vw;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.progress-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
  gap: 8px;
}

.stat-item {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-item.success {
  background-color: #f0f9eb;
  color: #52c41a;
}

.stat-item.processing {
  background-color: #fffbe6;
  color: #faad14;
}

.stat-item.error {
  background-color: #fff2f0;
  color: #f5222d;
}

.stat-item.total {
  background-color: #f0f2f5;
  color: #1890ff;
}

.stat-icon {
  font-size: 16px;
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.progress-item {
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #eee;
  transition: all 0.3s;
}

.progress-item.processing {
  border-color: #faad14;
  background-color: #fffbf0;
}

.progress-item.success {
  border-color: #52c41a;
  background-color: #f6ffed;
}

.progress-item.error {
  border-color: #f5222d;
  background-color: #fff1f0;
}

.progress-item-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 500;
}

.supplier-name {
  color: #333;
}

.status-badge {
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 4px;
}

.progress-item.processing .status-badge {
  background-color: #fff1b8;
  color: #faad14;
}

.progress-item.success .status-badge {
  background-color: #b7eb8f;
  color: #52c41a;
}

.progress-item.error .status-badge {
  background-color: #ffccc7;
  color: #f5222d;
}

.batch-info {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.error-message {
  font-size: 13px;
  color: #f5222d;
  padding: 8px;
  background-color: rgba(245, 34, 45, 0.05);
  border-radius: 4px;
  margin-top: 8px;
}

.progress-bar {
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 8px;
}

.progress-indicator {
  height: 100%;
  background-color: #faad14;
  transition: width 0.3s ease;
}

.cancel-btn {
  background-color: #f5222d;
  color: white;
}
</style>
     -->

    


<style scoped>
.progress-modal {
  width: 90vw;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.progress-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
  gap: 8px;
}

.stat-item {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-item.success {
  background-color: #f0f9eb;
  color: #52c41a;
}

.stat-item.processing {
  background-color: #fffbe6;
  color: #faad14;
}

.stat-item.error {
  background-color: #fff2f0;
  color: #f5222d;
}

.stat-item.total {
  background-color: #f0f2f5;
  color: #1890ff;
}

.stat-icon {
  font-size: 16px;
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.progress-item {
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #eee;
  transition: all 0.3s;
}

.progress-item.processing {
  border-color: #faad14;
  background-color: #fffbf0;
}

.progress-item.success {
  border-color: #52c41a;
  background-color: #f6ffed;
}

.progress-item.error {
  border-color: #f5222d;
  background-color: #fff1f0;
}

.progress-item-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 500;
}

.supplier-name {
  color: #333;
}

.status-badge {
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 4px;
}

.progress-item.processing .status-badge {
  background-color: #fff1b8;
  color: #faad14;
}

.progress-item.success .status-badge {
  background-color: #b7eb8f;
  color: #52c41a;
}

.progress-item.error .status-badge {
  background-color: #ffccc7;
  color: #f5222d;
}

.batch-info {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.error-message {
  font-size: 13px;
  color: #f5222d;
  padding: 8px;
  background-color: rgba(245, 34, 45, 0.05);
  border-radius: 4px;
  margin-top: 8px;
}

.progress-bar {
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 8px;
}

.progress-indicator {
  height: 100%;
  background-color: #faad14;
  transition: width 0.3s ease;
}
.scan-stock-out-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  background-color: #f5f7fa;
  color: #333;
  font-size: 14px;
}

.header {
  display: flex;
  align-items: center;
  padding: 3px;
  background-color: #032566;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px; /* 调整内边距来控制按钮整体大小 */
  border-radius: 4px; /* 可选：添加圆角 */
}

.icon-back {
  width: 24px; 
  height: 24px; 
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.back-btn:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

.title {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0;
  font-weight: 600;
}

.content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  box-sizing: border-box;
}

.status-banner {
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  transition: opacity 0.3s;
}

.status-banner.success {
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #e6f7d9;
}

.status-banner.error {
  background-color: #fff1f0;
  color: #f5222d;
  border: 1px solid #ffccc7;
}

.status-banner.warn {
  background-color: #fff7e6;
  color: #faad14;
  border: 1px solid #ffe8b6;
}

.form-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 18px;
  background-color: #42b983;
  margin-right: 8px;
  border-radius: 2px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.validation-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #f5222d;
  margin-bottom: 0;
}

#sourceDataInput, .form-group input, .form-group select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 44px;
}

#sourceDataInput:focus, .form-group input:focus, .form-group select:focus {
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.2);
}

#sourceDataInput {
  resize: none;
}

.btn {
  min-height: 44px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.source-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 8px;
}

.source-actions .parse-btn {
  grid-column: 1 / 2;
}

.source-actions .manual-input-btn {
  grid-column: 2 / 3;
}

.source-actions .clear-btn {
  grid-column: 1 / 3;
}

.parse-btn {
  background-color: #1890ff;
  color: #fff;
}

.parse-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.parse-btn:not(:disabled):hover {
  background-color: #096dd9;
}

.manual-input-btn {
  background-color: #faad14;
  color: #fff;
}

.manual-input-btn:hover {
  background-color: #d48806;
}

.clear-btn {
  background-color: #f5f7fa;
  color: #666;
  border: 1px solid #dcdfe6;
}

.clear-btn:hover {
  background-color: #eef0f3;
}

.submit-btn {
  width: 100%;
  height: 50px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  /* 新增：固定定位样式 */
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 0 16px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

.submit-btn:disabled {
  background-color: #a0d4ae;
  cursor: not-allowed;
}

.submit-btn:not(:disabled):hover {
  background-color: #359469;
}

.summary-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #f0f2f5;
}

.summary-item {
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.summary-label {
  color: #666;
  margin-right: 6px;
}

.summary-value {
  color: #333;
}

.empty-state {
  padding: 48px 0;
  text-align: center;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-text {
  font-size: 14px;
  line-height: 1.5;
}

.card-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.data-card {
  border: 1px solid #f0f2f5;
  border-radius: 8px;
  padding: 16px;
  transition: box-shadow 0.2s;
  background-color: #fff;
}

.data-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
}

.card-label {
  font-size: 14px;
  color: #1890ff;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 80%;
}

.card-delete {
  background: transparent;
  border: none;
  color: #999;
  font-size: 18px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
}

.card-delete:hover {
  background-color: #fff1f0;
  color: #f5222d;
}

.card-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.card-row {
  font-size: 13px;
  line-height: 1.5;
  display: flex;
}

.row-label {
  color: #666;
  min-width: 80px;
  font-weight: 500;
}

.row-value {
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-primary {
  color: #42b983;
  font-weight: 600;
}

/* 新增：加载动画样式 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-text {
  color: #666;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  padding: 0 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  backdrop-filter: blur(2px);
}

.modal-container {
  width: 100%;
  max-width: 500px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: modalFadeIn 0.3s;
}

.pda-optimized {
  max-height: 95vh;
  max-width: 95vw;
  padding: 0;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f2f5;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
}

.modal-close:hover {
  background-color: #f5f7fa;
  color: #333;
}

.modal-body {
  padding: 16px;
  overflow-y: auto;
  max-height: calc(95vh - 120px);
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #f0f2f5;
  /* 添加固定定位样式 */
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 10;
}

.modal-footer .btn {
  flex: 1;
}

.modal-cancel {
  background-color: #f5f7fa;
  color: #666;
  border: 1px solid #dcdfe6;
}

.modal-cancel:hover {
  background-color: #eef0f3;
}

.modal-confirm {
  background-color: #42b983;
  color: #fff;
}

.modal-confirm:disabled {
  background-color: #a0d4ae;
  cursor: not-allowed;
}

.modal-confirm:not(:disabled):hover {
  background-color: #359469;
}

.preview-summary {
  background-color: #f9fafb;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #f0f2f5;
}

.preview-summary .summary-title {
  font-size: 16px;
  margin-bottom: 16px;
  color: #333;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f2f5;
}

.summary-table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 16px;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background-color: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.summary-table th {
  background-color: #f5f7fa;
  color: #666;
  font-weight: 600;
  padding: 12px 8px;
  text-align: left;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.summary-header:nth-child(1) { width: 30%; }
.summary-header:nth-child(2) { width: 30%; }
.summary-header:nth-child(3) { width: 20%; text-align: center; }
.summary-header:nth-child(4) { width: 20%; text-align: center; }

.summary-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;
  word-wrap: break-word;
  word-break: break-all;
}

.summary-row:last-child td {
  border-bottom: none;
}

.supplier-cell { color: #1890ff; }
.material-cell { color: #333; }
.summary-row td:nth-child(3),
.summary-row td:nth-child(4) { text-align: center; }

.total-row {
  background-color: #f6ffed;
  font-weight: 600;
}

.warehouse-info-summary {
  font-size: 13px;
  color: #666;
  background-color: #fff;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #f0f2f5;
}

.warehouse-info-summary p {
  margin: 6px 0;
  line-height: 1.5;
}

.preview-detail .detail-title {
  font-size: 16px;
  margin-bottom: 16px;
  color: #333;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f2f5;
}

.detail-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.detail-card {
  border: 1px solid #f0f2f5;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
  transition: box-shadow 0.2s;
}

.detail-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.detail-card-header {
  background-color: #f5f7fa;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.detail-card-title {
  font-size: 14px;
  color: #1890ff;
  font-weight: 600;
}

.detail-card-body {
  padding: 12px 16px;
}

.detail-item {
  display: flex;
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.5;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #666;
  min-width: 80px;
  font-weight: 500;
}

.detail-value {
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.highlight-item {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px dashed #e0e0e0;
}

.highlight-item .detail-value {
  color: #42b983;
  font-weight: 600;
}

.required {
  color: #f5222d;
  margin-left: 2px;
}

@media (max-width: 480px) {
  .source-actions {
    grid-template-columns: 1fr;
  }
  
  .source-actions .parse-btn,
  .source-actions .manual-input-btn,
  .source-actions .clear-btn {
    grid-column: 1 / 2;
    width: 100%;
  }
  
  .card-row {
    flex-wrap: wrap;
  }
  
  .row-label {
    min-width: auto;
    margin-right: 8px;
    margin-bottom: 4px;
  }

  .summary-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-cards {
    grid-template-columns: 1fr;
  }
  
  .summary-table th,
  .summary-table td {
    padding: 10px 6px;
    font-size: 12px;
  }
  
  .modal-body {
    padding: 12px;
    max-height: calc(95vh - 100px);
  }
  
  .modal-header, .modal-footer {
    padding: 12px;
  }
  
  .preview-summary {
    padding: 12px;
  }
}
</style>
